/**
 * ProtectedRoute Component
 * Provides route-level permission checking
 * Redirects to unauthorized page if user lacks required permission
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useGetCurrentUser } from '@/hooks/app';
import { hasPermission, type Permissions } from '@/utils/permission';
import { Permission } from '@/types/permissions.types';
import { ROUTESPATH } from '@/routes';
import { Spin } from 'antd';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission: Permission | string;
}

/**
 * Wraps a route to require a specific permission
 * Redirects to /unauthorized if user lacks permission
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const { data: currentUser, isLoading } = useGetCurrentUser();

  // Show loading spinner while checking permissions
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const permissions: Permissions = currentUser?.permission;

  // Check if user has the required permission
  if (!hasPermission(permissions, requiredPermission)) {
    return <Navigate to={ROUTESPATH.UNAUTHORIZED} replace />;
  }

  // User has permission, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
